import React, { useMemo } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_Row,
  type MRT_ColumnDef,
} from 'material-react-table';
import { Box, Button } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export type MRTTableAdapterProps<T extends Record<string, any>> = {
  columns: MRT_ColumnDef<T, any>[];
  data: T[];
  enableRowSelection?: boolean;
  columnFilterDisplayMode?: 'popover' | 'subheader';
  paginationDisplayMode?: 'pages' | 'default';
  positionToolbarAlertBanner?: 'bottom' | 'top' | 'none' | 'head-overlay';
  enableColumnFilters?: boolean;
  enableGlobalFilter?: boolean;
  enablePagination?: boolean;
  initialPageSize?: number;
  rowsPerPageOptions?: number[];
  extraTopToolbarActions?: (args: { table: any }) => React.ReactNode;
};

function MRTTableAdapter<T extends Record<string, any>>({
  columns,
  data,
  enableRowSelection = true,
  columnFilterDisplayMode = 'popover',
  paginationDisplayMode = 'pages',
  positionToolbarAlertBanner = 'bottom',
  enableColumnFilters = true,
  enableGlobalFilter = false,
  enablePagination = true,
  initialPageSize = 50,
  rowsPerPageOptions = [10, 25, 50, 100],
  extraTopToolbarActions,
}: MRTTableAdapterProps<T>) {
  
  const handleExportRows = (rows: MRT_Row<T>[]) => {
    const doc = new jsPDF();
    const tableData = rows.map((row) => Object.values(row.original));
    const tableHeaders = columns.map((c) => c.header as string);
    
    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
    });
    
    doc.save('mrt-pdf-export.pdf');
  };

  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection,
    columnFilterDisplayMode,
    paginationDisplayMode,
    positionToolbarAlertBanner,
    enableGlobalFilter,
    enableColumnFilters,
    enablePagination,
    enableSorting: true,
    enableRowVirtualization: false,
    enableStickyHeader: true,
    muiTableContainerProps: {
      sx: { maxHeight: 640 },
    },
    // Provide stable row ids and a sensible default page size
    getRowId: (row: any) => {
      if (row?.id !== undefined && row?.id !== null && row?.id !== '') return String(row.id);
      if (row?.workflow_id) return `wf:${row.workflow_id}`;
      if (row?.created_at) return `created:${row.created_at}`;
      if (row?.description && row?.received_date) return `d:${row.description}|r:${row.received_date}`;
      // Final fallback: stringify limited fields to keep key stable across renders
      try { return `k:${String(row?.description || '')}|${String(row?.product_id || '')}`; } catch { return Math.random().toString(36).slice(2); }
    },
    initialState: {
      pagination: { pageIndex: 0, pageSize: initialPageSize },
    },
    muiPaginationProps: {
      rowsPerPageOptions,
    },
    muiTablePaperProps: {
      sx: {
        borderRadius: 2,
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
      }
    },
    muiTopToolbarProps: {
      sx: { borderBottom: '1px solid #e5e7eb', backgroundColor: 'transparent' }
    },
    muiBottomToolbarProps: {
      sx: { borderTop: '1px solid #e5e7eb', backgroundColor: 'transparent' }
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: 'transparent',
        fontWeight: 600,
        fontSize: '0.85rem',
        py: 1.25,
        borderBottom: '1px solid #e5e7eb'
      }
    },
    muiTableProps: { sx: { backgroundColor: 'transparent', color: 'inherit' } },
    muiTableBodyProps: { sx: { backgroundColor: 'transparent', color: 'inherit' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.9rem', py: 1.0 } },
    muiTableBodyRowProps: {
      hover: true,
      sx: {
        '&:nth-of-type(odd)': {
          backgroundColor: 'rgba(255,255,255,0.02)'
        }
      }
    },
    muiSearchTextFieldProps: { variant: 'outlined' },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: '16px', padding: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          disabled={table.getPrePaginationRowModel().rows.length === 0}
          onClick={() => handleExportRows(table.getPrePaginationRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export All Rows
        </Button>
        <Button
          disabled={table.getRowModel().rows.length === 0}
          onClick={() => handleExportRows(table.getRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export Page Rows
        </Button>
        <Button
          disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
          onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export Selected Rows
        </Button>
        {extraTopToolbarActions ? (
          <Box sx={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
            {extraTopToolbarActions({ table })}
          </Box>
        ) : null}
      </Box>
    ),
  });

  return <MaterialReactTable table={table} />;
}

export default MRTTableAdapter;




